import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, Grid, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import { usePostHog } from 'posthog-js/react';
import * as yup from 'yup';
import { Field, FieldProps, Formik } from 'formik';
import SharedMobileTimePicker from '../../../common/ui/SharedMobileTimePicker';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useLanguage } from '../../../common/GeneralUtils';
import { AccountData, CourseService, CourseSessionData, OrderPageData, OrderPageTypes } from '../../../api';
import { WorkshopCircle } from '../../../common/ui/WorkshopCircle';
import { getDateFormat } from '../../../common/getDateAndTimeFormat';
import { useEvents } from '../../swr/useEvents';
import { RotatingLoader } from '../../../common/ui/RotatingLoader';
import { UsersMultiSelect } from '../../components/UsersMultiSelect';
import { getEffectivePageLocationId } from '../../../common/filter/locationGrouping';

type SessionState = {
  date: Moment;
  time: Moment;
  dateOpen: boolean;
  eventId?: string;
};

type CourseFormValues = {
  order_page_id: string;
  tickets: number;
  employee_ids: string[];
  sessions: SessionState[];
};

const validationSchema = yup.object().shape({
  tickets: yup.number().min(1).required(),
  sessions: yup.array().min(1).required(),
});

const defaultSession = (): SessionState => ({
  date: moment(),
  time: moment().set({ hour: 9, minute: 0 }),
  dateOpen: false,
});

export const EditCourseDialogContent = () => {
  const {
    closeExperienceAvailabilityDrawer,
    startDate: calendarStartDate,
    endDate: calendarEndDate,
    experienceAvailabilityDrawerState: { event: stateEvent },
    users,
  } = useCalendar();

  const { reFetch } = useEvents({
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  });

  const { t } = useTranslation();
  const posthog = usePostHog();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);

  const language = useLanguage();
  const courseOrderPages = getOrderPages(account).filter(
    (x: OrderPageData) => x.order_page_type === OrderPageTypes.COURSE,
  );

  const isEditCourse = !!stateEvent?.course_id;

  const [fetchingCourse, setFetchingCourse] = useState(false);
  const [fetchedInitialValues, setFetchedInitialValues] = useState<CourseFormValues | null>(null);

  useEffect(() => {
    if (!isEditCourse) return;

    const fetchCourse = async () => {
      setFetchingCourse(true);
      try {
        const course = await CourseService.getCourse(stateEvent.course_id!);

        setFetchedInitialValues({
          order_page_id: course.order_page_id || courseOrderPages[0]?.id || '',
          tickets: course.tickets,
          employee_ids: course.employee_ids || [],
          sessions:
            course.sessions && course.sessions.length > 0
              ? course.sessions.map((s: CourseSessionData) => ({
                  date: moment(s.date, 'YYYY-MM-DD'),
                  time: moment().startOf('day').add(s.time, 'seconds'),
                  dateOpen: false,
                  eventId: s.event_id,
                }))
              : [defaultSession()],
        });
      } catch (error) {
        console.error('Failed to fetch course:', error);
      } finally {
        setFetchingCourse(false);
      }
    };

    void fetchCourse();
  }, [isEditCourse]);

  const defaultInitialValues = useMemo<CourseFormValues>(
    () => ({
      order_page_id: courseOrderPages[0]?.id || '',
      tickets: 20,
      employee_ids: [],
      sessions: [defaultSession()],
    }),
    [],
  );

  const initialValues = fetchedInitialValues || defaultInitialValues;

  if (courseOrderPages.length === 0) {
    return (
      <Grid container p={3} justifyContent="center">
        <Box>{t('calendar.edit_course.no_course_pages')}</Box>
      </Grid>
    );
  }

  if (fetchingCourse) {
    return (
      <Grid container p={3} justifyContent="center">
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const selectedOrderPage = courseOrderPages.find((x: OrderPageData) => x.id === values.order_page_id);

        if (values.sessions.length === 0) return;
        if (!isEditCourse && !selectedOrderPage) return;

        const sessionData: CourseSessionData[] = values.sessions.map((session) => ({
          date: session.date.format('YYYY-MM-DD'),
          time: session.time.hours() * 3600 + session.time.minutes() * 60,
          ...(session.eventId ? { event_id: session.eventId } : {}),
        }));

        try {
          if (isEditCourse) {
            await CourseService.updateCourse({
              course_id: stateEvent.course_id!,
              tickets: values.tickets,
              sessions: sessionData,
              employee_ids: values.employee_ids,
            });
            posthog.capture('Course availability updated', { courseId: stateEvent.course_id });
          } else {
            await CourseService.createCourse({
              account_id: account.id,
              experience_id: account.experiences[0].id,
              order_page_id: values.order_page_id,
              tickets: values.tickets,
              sessions: sessionData,
              employee_ids: values.employee_ids,
            });
            posthog.capture('Course availability created', {
              orderPageId: values.order_page_id,
              sessionsCount: values.sessions.length,
            });
          }

          await reFetch();
          closeExperienceAvailabilityDrawer();
        } catch (error) {
          console.error(`Failed to ${isEditCourse ? 'update' : 'create'} course:`, error);
        }
      }}>
      {({ values, setFieldValue, isSubmitting, submitForm }) => {
        const selectedOrderPage = courseOrderPages.find((x: OrderPageData) => x.id === values.order_page_id);
        const requiredSessions = selectedOrderPage?.course_sessions_number;
        const sessionsRemaining = requiredSessions ? requiredSessions - values.sessions.length : 0;
        const hasValidSessionCount = !requiredSessions || values.sessions.length === requiredSessions;

        const addSession = () => {
          const lastSession = values.sessions[values.sessions.length - 1];
          void setFieldValue('sessions', [
            ...values.sessions,
            {
              date: lastSession ? lastSession.date.clone().add(7, 'days') : moment(),
              time: lastSession ? lastSession.time.clone() : moment().set({ hour: 9, minute: 0 }),
              dateOpen: false,
            },
          ]);
        };

        const removeSession = (index: number) => {
          if (values.sessions.length > 1) {
            void setFieldValue(
              'sessions',
              values.sessions.filter((_, i) => i !== index),
            );
          }
        };

        return (
          <Grid container flexDirection="column" sx={{ height: '100%' }}>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              sx={{
                flex: 1,
                overflowY: 'auto',
                gap: 1.5,
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              <FormControl fullWidth>
                <Select
                  value={values.order_page_id}
                  disabled={isEditCourse}
                  renderValue={() =>
                    selectedOrderPage ? (
                      <Grid container alignItems="center" gap={1}>
                        <WorkshopCircle account={account} orderPageId={selectedOrderPage.id} language={language} />
                        <Grid item>{selectedOrderPage.label[language]}</Grid>
                      </Grid>
                    ) : null
                  }
                  onChange={(event) => {
                    void setFieldValue('order_page_id', event.target.value);
                  }}>
                  {courseOrderPages.map((x: OrderPageData) => (
                    <MenuItem key={`courseOrderPages-${x.id}`} value={x.id}>
                      <Grid container alignItems="center" gap={1}>
                        <WorkshopCircle account={account} orderPageId={x.id} language={language} />
                        {x.label[language]}
                      </Grid>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Field name="tickets">
                {({ field, form }: FieldProps<number>) => (
                  <TextField
                    {...field}
                    label={t('calendar.edit_course.tickets')}
                    type="number"
                    InputProps={{ inputProps: { min: 1 } }}
                    value={field.value ?? ''}
                    onChange={(e) => form.setFieldValue('tickets', parseFloat(e.target.value) || 0)}
                  />
                )}
              </Field>

              <UsersMultiSelect
                users={users}
                selectedUsersIds={values.employee_ids}
                setSelectedUsersIds={(ids) => setFieldValue('employee_ids', ids)}
                locationId={getEffectivePageLocationId(
                  courseOrderPages.find((page) => page.id === values.order_page_id)?.location_id,
                  account,
                )}
              />

              <Box>
                <Box fontWeight="bold" mb={1}>
                  <Typography>
                    {t('calendar.edit_course.sessions')} ({values.sessions.length}
                    {requiredSessions ? `/${requiredSessions}` : ''})
                  </Typography>
                </Box>
                {requiredSessions && !hasValidSessionCount && (
                  <Box mb={1}>
                    <Typography color={'error.main'}>
                      {sessionsRemaining > 0
                        ? t('calendar.edit_course.sessions_remaining', { count: sessionsRemaining })
                        : t('calendar.edit_course.too_many_sessions', { count: Math.abs(sessionsRemaining) })}
                    </Typography>
                  </Box>
                )}
                {values.sessions.map((session, index) => (
                  <Grid container key={index} alignItems="center" gap={1} mb={1.5}>
                    <Grid item xs={5}>
                      <DatePicker
                        open={session.dateOpen}
                        onOpen={() => setFieldValue(`sessions[${index}].dateOpen`, true)}
                        onClose={() => setFieldValue(`sessions[${index}].dateOpen`, false)}
                        label={`${t('calendar.edit_course.session')} ${index + 1}`}
                        value={session.date}
                        format={longDateFormat}
                        onChange={(newValue) => newValue && setFieldValue(`sessions[${index}].date`, newValue)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <SharedMobileTimePicker
                        minTime={moment().set({ hour: 6, minute: 0, second: 0, millisecond: 0 })}
                        label={t('calendar.edit_course.start_time')}
                        value={session.time}
                        minutesStep={15}
                        onChange={(newValue) => newValue && setFieldValue(`sessions[${index}].time`, newValue)}
                      />
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => removeSession(index)}
                        disabled={values.sessions.length === 1}
                        size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <IconButton onClick={addSession} color="primary">
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              <LoadingButton
                type="submit"
                color="primary"
                loading={isSubmitting}
                variant="contained"
                onClick={submitForm}
                disabled={values.sessions.length === 0 || !hasValidSessionCount}
                sx={{ minWidth: 100 }}>
                {isEditCourse ? t('save_changes') : t('calendar.edit_course.create')}
              </LoadingButton>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
};
